import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  ViewChild,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';

import { MatSelectModule } from '@angular/material/select';
import type { ICellEditorParams } from 'ag-grid-community';

import { Observable, Subscription } from 'rxjs';
import { CiaoFormsModule } from '~app/components/shared/forms/forms.module';
import { FieldComponent } from '../forms/field/field.component';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

type CellEditorParams = {
  options$: any;
  validators: [];
  /** When true, commit the value as soon as the user picks an option. */
  commitOnSelect?: boolean;
};
@Component({
  selector: 'ciao-cell-editor',
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    ReactiveFormsModule,
    CiaoFormsModule,
  ],
  templateUrl: './cell-editor.component.html',
  styleUrl: './cell-editor.component.less',
})
export class CellEditorComponent {
  constructor(private readonly cdr: ChangeDetectorRef) {}
  @ViewChild(FieldComponent) FormField: FieldComponent;
  @HostBinding('class.has-error')
  get isErrored(): boolean {
    return this.fieldControl.invalid;
  }
  params: ICellEditorParams & CellEditorParams;
  ICellEditorParams;
  options$: Observable<any>;
  fieldType: string;
  placeholder: string;
  fullSize: boolean = false;
  validators: ValidatorFn[] = [];
  subscriptions = new Subscription();

  public set value(val) {
    this.fieldControl.setValue(val, {
      emitEvent: false,
    });
  }
  public get value() {
    return this.fieldControl.value;
  }
  getValue() {
    return this.value;
  }

  fieldControl = new FormControl<string>('');

  agInit(params: any) {
    this.params = params;
    this.value = params.value;
    this.options$ = this.params.options$;
    this.setupFieldProperties(params);
    this.setupFieldStatusListener();
    this.setupCommitOnSelect();
    this.refresh();
  }

  /**
   * For single-select comboboxes, commit the value as soon as the user picks an
   * option instead of waiting for the cell to blur. ag-grid only writes the
   * editor value back to the row (firing onCellValueChanged) when editing
   * stops, so without this the parent's row data — and anything derived from
   * it, like the checkout button's enabled state — wouldn't update until the
   * user clicked another field. Opt-in via the `commitOnSelect` cellEditorParam.
   */
  setupCommitOnSelect() {
    if (!this.params?.commitOnSelect) {
      return;
    }
    this.subscriptions.add(
      this.fieldControl.valueChanges.subscribe(() => {
        // Defer so we don't tear the editor down synchronously from inside the
        // select's own change handler.
        setTimeout(() => this.params.api?.stopEditing());
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  setupFieldProperties(params) {
    this.fieldType = params?.fieldType;
    this.placeholder = params?.placeholder;
    // Wait until ciao-form-field is initialized;
    this.cdr.detectChanges();
    // True Single Click.
    this.FormField?.Focus();
  }

  setupFieldStatusListener() {
    // Grow row height when errors
    this.subscriptions.add(
      this.fieldControl.statusChanges
        .pipe(
          map((_) => this.fieldControl?.errors),
          distinctUntilChanged(),
          tap((errors) => {
            //TODO: grow width of column if errors (currently widths larger + wrap fields with validation)
            let defaultRowHeight = 40;
            let defaultPinnedRowHeight = 50;
            if (this.params.node.rowPinned) {
              defaultRowHeight = defaultPinnedRowHeight;
            }
            if (errors) {
              // we have errors so mark the field as touched and dirty
              let numErrors = Object.keys(errors).length;
              if (this.params.colDef.field === 'plotNumber') {
                numErrors = numErrors + 2;
              }
              let calculatedHeight = numErrors * 20 + defaultRowHeight + 15;
              this.fieldControl.markAsTouched();
              this.fieldControl.markAsDirty();
              this.params.node?.setRowHeight(calculatedHeight);
              this.params.api.onRowHeightChanged();
            } else {
              this.params.node?.setRowHeight(defaultRowHeight);
              this.params.api.onRowHeightChanged();
            }
          })
        )
        .subscribe()
    );
  }

  compareOptionForSelect(a: any, b: any) {
    if (a === b) {
      return true;
    }
    if (a && b && a?.id === b?.id) {
      // compare the ids, but not if a or b is null.
      return true;
    }
    return false;
  }

  refresh() {
    if (this.params.options$) {
      this.options$ = this.params?.options$;
    }
    if (this.params.validators) {
      this.fieldControl.addValidators(this.params.validators);
    }
    return true;
  }
}
